import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Upload, Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

export default function AlbumManager() {
  const { t } = useTranslation();
  const { id } = useParams();
  const inputRef = useRef(null);
  const [album, setAlbum] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.get(`/albums/${id}`).then((r) => setAlbum(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: uploaded } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/gallery/images", { album_id: id, path: uploaded.path, caption: "" });
      }
      toast.success(t("admin.gallery.toastImagesUploaded", { count: files.length }));
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const removeImage = async () => {
    const imgId = confirmId;
    setConfirmId(null);
    try {
      await api.delete(`/gallery/images/${imgId}`);
      toast.success(t("admin.gallery.toastImageDeleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setCover = async (path) => {
    try {
      await api.put(`/albums/${id}`, { cover_path: path });
      toast.success(t("admin.gallery.toastCoverSet"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateCaption = async (imgId, caption, current) => {
    if ((current || "") === (caption || "")) return;
    try {
      await api.put(`/gallery/images/${imgId}`, { caption });
      setAlbum((prev) =>
        prev ? { ...prev, images: prev.images.map((i) => (i.id === imgId ? { ...i, caption } : i)) } : prev
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!album) return <div className="text-slate-500 text-sm">{t("admin.common.loading")}</div>;

  return (
    <div data-testid="album-manager" className="space-y-8">
      <div>
        <Link to="/admin/galerie" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-bordeaux mb-3">
          <ArrowLeft size={14} /> {t("admin.gallery.backList")}
        </Link>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">{album.title}</h1>
            {album.description && <p className="text-slate-600 mt-2 max-w-2xl">{album.description}</p>}
          </div>
          <div>
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => handleFiles(e.target.files)} data-testid="album-upload-input" />
            <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="album-upload-btn" className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm">
              {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
              {t("admin.gallery.uploadBtn")}
            </Button>
          </div>
        </div>
      </div>

      {album.images.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-sm p-16 text-center text-slate-500">
          <Upload className="mx-auto mb-3" size={22} />
          <p className="text-sm">{t("admin.gallery.emptyUpload")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {album.images.map((img) => (
            <div key={img.id} data-testid={`album-image-${img.id}`} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="aspect-[4/3] bg-beige overflow-hidden relative">
                <img src={fileUrl(img.path)} alt={img.caption || ""} className="w-full h-full object-cover" />
                {album.cover_path === img.path && (
                  <div className="absolute top-2 left-2 bg-bordeaux text-beige-light text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm">
                    {t("admin.gallery.coverBadge")}
                  </div>
                )}
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 block">
                    {t("admin.gallery.captionLabel")}
                  </Label>
                  <Textarea
                    defaultValue={img.caption || ""}
                    placeholder={t("admin.gallery.captionPlaceholder")}
                    data-testid={`image-caption-${img.id}`}
                    onBlur={(e) => updateCaption(img.id, e.target.value, img.caption)}
                    rows={3}
                    className="rounded-sm border-slate-300 text-sm resize-y"
                  />
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                  <button onClick={() => setCover(img.path)} data-testid={`image-set-cover-${img.id}`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-bordeaux">
                    <Star size={12} /> {t("admin.gallery.setCover")}
                  </button>
                  <button onClick={() => setConfirmId(img.id)} data-testid={`image-delete-${img.id}`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-red-600">
                    <Trash2 size={12} /> {t("admin.common.delete")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.common.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.common.confirmDeleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="image-delete-cancel">{t("admin.common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={removeImage} data-testid="image-delete-confirm" className="bg-red-600 hover:bg-red-700">
              {t("admin.common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
